import { useState, useContext, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Collapse, Divider, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import dayjs from 'dayjs';

import AuthContext from '../../context';
import { get, post } from '../../utils/api-services';
import helpers from '../../utils/helpers';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    buttonContainer: {
        marginBottom: 5,
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center'
    },
    storeBtn: {
        marginLeft: 15,
        ...themeColors.blueBtn
    }
}));

const defRecipe = { id: 0, name: 'Choose a recipe' };
const WorkPlanCreate = ({ open, permissions, onStore }) => {

    const classes = useStyles();
    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const isMounted = useRef(false);
    const [state, setState] = useState({
        recipes: [defRecipe],
        form: {
            date: dayjs().format('YYYY-MM-DD'),
            recipe: defRecipe,
            qty: 0
        }
    });

    useEffect(() => {
        if (open && !isMounted.current) {
            makeRequest();
            isMounted.current = true;
        }
    }, [open]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/kometWorkPlan/create');
        if (response.status === 200) {
            setState(prev => ({ ...prev, recipes: response.recipes }));
        } else {
            console.log('[WorkPlanCreate] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, form: { ...prev.form, [target.name]: target.value } }));

    const validate = () => {
        const { date, recipe, qty } = state.form;

        if (!permissions.create_attr) {
            showToast('You have no permissions for this action', 'error', 6500);
            return false;
        }
        if (!date || !recipe.id || !Number(qty)) {
            showToast('All fields are required', 'error');
            return false;
        }

        const cond = window.confirm('Confirm if all the data is correct');
        return cond;
    }

    const store = async () => {
        const cond = validate();
        if (cond) {
            showLoader(true);
            const response = await post('/kometWorkPlan/store', state.form);
            if (response.status === 200) {
                showToast(response.message);
                onStore();
                clear();
            } else {
                console.log('[WorkPlanCreate] store error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const clear = () => setState(prev => ({ ...prev, form: { date: dayjs().format('YYYY-MM-DD'), recipe: defRecipe, qty: 0 } }));

    return (
        <Collapse in={open}>
            <Divider />
            <h3>{'Create new work plan'}</h3>
            <Grid container>
                <Grid item xs={12} md={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Date'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='date'
                                name='date'
                                placeholder='Date'
                                value={state.form.date}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={12} lg={3} xl={2}>
                    <FormGroup>
                        <FormLabel>{'Recipe'}</FormLabel>
                        <Autocomplete
                            options={state.recipes}
                            value={state.form.recipe}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => setState(prev => ({ ...prev, form: { ...prev.form, recipe: value } }))}
                            renderInput={params => <TextField {...params} placeholder='Recipe' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={12} md={2} xl={1}>
                    <FormGroup>
                        <FormLabel>{'Qty'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='number'
                                name='qty'
                                placeholder='Qty'
                                value={state.form.qty}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>

                <Grid item xs={12} md={4} xl={6} className={classes.buttonContainer}>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={clear}
                    >
                        {'Cancel'}
                    </Button>
                    {
                        (permissions.create_attr) &&
                        <Button
                            variant='contained'
                            className={classes.storeBtn}
                            onClick={store}
                        >
                            {'Store'}
                        </Button>
                    }
                </Grid>
            </Grid>
        </Collapse>
    );
}

export default WorkPlanCreate;